"use client";

import { useCallback } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

/**
 * Validates that a returnUrl is a safe, same-origin destination before it is
 * used for navigation. Returns the cleaned path when safe, otherwise null.
 *
 * This guards against open-redirect attacks: the returnUrl ultimately reaches
 * `router.push()`, which performs a hard navigation for absolute or
 * protocol-relative URLs. An attacker could craft a protected-page link such as
 * `/page?returnUrl=https://evil.com` (or `//evil.com`) to bounce an
 * authenticated user off-platform after login.
 *
 * Only path-absolute URLs (a single leading slash followed by a path) are
 * allowed. This rejects:
 *  - absolute URLs ("https://evil.com", "javascript:...")
 *  - protocol-relative URLs ("//evil.com")
 *  - the backslash variants browsers normalise to them ("/\\evil.com")
 */
export function sanitizeReturnUrl(
	returnUrl: string | null | undefined,
): string | null {
	if (!returnUrl) {
		return null;
	}

	// Browsers strip tab/newline/carriage-return characters from URLs before
	// navigating, so an attacker could hide a protocol-relative URL behind them
	// (e.g. "/\t/evil.com" -> "//evil.com"). Strip them before validating so we
	// check what the browser will actually navigate to.
	const normalised = returnUrl.replace(/[\t\n\r]/g, "");

	const isSafeInternalPath =
		normalised.startsWith("/") &&
		!normalised.startsWith("//") &&
		!normalised.startsWith("/\\");

	return isSafeInternalPath ? normalised : null;
}

/**
 * Builds an href to the login page that remembers where the user was, so they
 * can be returned there after authenticating. The returnUrl is sanitised to a
 * safe same-origin path and always encoded.
 */
export function buildLoginHref(returnUrl: string | null | undefined): string {
	const safeReturnUrl = sanitizeReturnUrl(returnUrl);
	return safeReturnUrl
		? `/login?returnUrl=${encodeURIComponent(safeReturnUrl)}`
		: "/login";
}

/**
 * Returns a callback that sends the user to the login page, preserving their
 * current location as the returnUrl.
 *
 * Resolution order for the returnUrl:
 *  1. an explicit `returnUrl` query param, if present;
 *  2. otherwise the current location (path + query).
 *
 * When already on a `/login*` page and no explicit returnUrl is set, no
 * returnUrl is attached, which avoids redirecting the login page back to itself.
 */
export function useRedirectToLogin(): () => void {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	return useCallback(() => {
		const queryString = searchParams.toString();
		const currentUrl = `${pathname}${queryString ? `?${queryString}` : ""}`;
		const returnUrl =
			sanitizeReturnUrl(searchParams.get("returnUrl")) ??
			(pathname.startsWith("/login") ? null : currentUrl);
		router.push(buildLoginHref(returnUrl));
	}, [router, pathname, searchParams]);
}
